import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import FormNoteAttachmentsView from "@cayuse/shared-react.components.form-note-attachments-view";
import { isNumber } from "@cayuse/shared-react.utils.type-checks";

import {
    spUrl,
    notifyError,
    convertToPlainText,
    mapAdminNotesResponse
} from "utils";
import { downloadFile, downloadMultipartFile, save } from "utils/proxy";
import { validate as uuidValidate } from "uuid";
import {
    ROLE_SP_SYSTEM_ADMINISTRATOR,
    ADMIN_ONLY_NOTES_MAX_LENGTH
} from "globals/constants";
import { useHasRoles, useDictionary } from "hooks";
import { proposalSelectors, shellSelectors, tenantSelectors } from "store/selectors";
import { api } from "services";
import { attachments } from "services/api/attachments";


const AdminNoteUrls = {
    downloadAttachment: (proposalId, attachmentId) =>
        spUrl(`api/notes/proposals/${proposalId}/attachments/${attachmentId}/content`),
    uploadAttachment: (proposalId) => spUrl(`api/notes/proposals/${proposalId}/attachments`),
};

const ProposalAdminOnlyView = ({ setIsUploading }) => {
    const { proposalId } = useParams();
    const { fullName, personId } = useSelector(shellSelectors.user);
    const { projectAttachmentTypes } = useSelector(tenantSelectors.tenant);

    const {
        [ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
    } = useHasRoles([
        ROLE_SP_SYSTEM_ADMINISTRATOR,
    ]);

    const dictionary = {
        ...useDictionary("common.notes"),
        ...useDictionary("pagingControls"),
        ...useDictionary("proposals.attachments"),
    };
    const [ userNames, setUserNames ] = useState([]);

    const canEditNotes = (userId) => isSystemAdmin || userId === personId;

    const deleteItem = async (noteId) => {
        return api.proposal.deleteNote({ noteId, proposalId });
    };

    const deleteAttachment = async (attachmentId) => {
        return api.proposal.deleteAttachment({ attachmentId, proposalId });
    };

    const fetchItems = async (pageNumber, itemsPerPage) => {
        const response = await api.proposal.getNotes(
            { proposalId },
            {
                page: isNumber(pageNumber) ? pageNumber : undefined,
                size: isNumber(itemsPerPage) ? itemsPerPage : undefined,
            },
        );

        return mapAdminNotesResponse({
            response,
            userNames,
            setUserNames,
            dictionaryUnknownUser: dictionary.unknownUser,
        });
    };

    const validateNoteLength = message => {
        const decodedMessage = decodeURI(message);
        const plainTextMessage = convertToPlainText(decodedMessage);
        if (plainTextMessage.length > ADMIN_ONLY_NOTES_MAX_LENGTH) {
            notifyError(dictionary.msgTooLong ?? "Note is too long.");
            return false;
        }

        return true;
    }
    const saveItem = async (message) => {
        if (validateNoteLength(message)) {
            return api.proposal.saveNote({ proposalId }, { message: message });
        }
    };
    const updateItem = async (noteId, payload) => {
        if (validateNoteLength(payload.message)) {
            return api.proposal.updateNote(
                { noteId, proposalId },
                {
                    ...payload,
                    createdByFullName: undefined,
                    updatedByFullName: undefined,
                },
            );
         }
    };
    const uploadAttachment = async (payload) => {
        if(payload.noteId && !uuidValidate(payload.noteId)) {
            delete payload.noteId;
        }
        const url = AdminNoteUrls.uploadAttachment(proposalId);
        const file = payload.attachment;
        
        const fileMetadata = await attachments.upload(file);

        let postUrl = `${url}?`;
        for(const property of ["attachmentType", "noteId"]) {
            if (property in payload) {
                const queryParam = `${property}=${encodeURIComponent(payload[property])}&`;
                postUrl = postUrl + queryParam
            }
        }

        if (!fileMetadata) {
            const error = new Error("Failed to upload file. Please try again.");
            notifyError(error.message, error);

            return;
        }

        const response = await save(postUrl, fileMetadata);

        return { ...response, uploadedBy: fullName };
    };

    const downloadAttachment = async (attachmentId, fileName, attachment) => {
        const filePath = attachment.filePath
        if (filePath?.startsWith("platform://")){
          return downloadMultipartFile({
            id: filePath.slice(11),
            fileName
          });
        }
        else {
            const url = AdminNoteUrls.downloadAttachment(proposalId, attachmentId);
            return downloadFile(url, fileName);
        }
    };

    return (
        <FormNoteAttachmentsView
          attachmentTypes={projectAttachmentTypes}
          canEditNotes={canEditNotes}
          deleteAttachment={deleteAttachment}
          deleteItem={deleteItem}
          dictionary={dictionary}
          downloadFile={downloadAttachment}
          fetchItems={fetchItems}
          fullName={fullName}
          saveItem={saveItem}
          updateItem={updateItem}
          uploadAttachment={uploadAttachment}
          uploadingStateChanged={state => setIsUploading(state.isUploading)}
          //only for Netarts release
          hideSearchBox={true}
        />
    );
};

export default ProposalAdminOnlyView;
